/**
 * MotorBox Product Configurator
 * Interactive visual product configuration with color swatches and material selection
 */

class ProductConfigurator {
  constructor(container, productData) {
    this.container = container;
    this.productData = productData;
    this.selectedOptions = [];
    this.currentVariant = null;

    this.init();
  }

  init() {
    if (!this.container || !this.productData) {
      console.error('ProductConfigurator: Missing container or product data');
      return;
    }

    // Initialize selected options from current variant
    this.initializeSelectedOptions();

    // Bind event listeners
    this.bindEvents();

    // Find and set current variant
    this.updateVariant();
  }

  initializeSelectedOptions() {
    const product = this.productData;
    const currentVariant = product.selected_or_first_available_variant;

    if (currentVariant && currentVariant.options) {
      this.selectedOptions = [...currentVariant.options];
    } else if (product.options) {
      // Initialize with first value of each option
      this.selectedOptions = product.options.map(option => {
        return option.values ? option.values[0] : '';
      });
    }
  }

  bindEvents() {
    // Listen for swatch/button clicks
    const swatches = this.container.querySelectorAll('[data-option-value]');

    swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        const optionValue = swatch.dataset.optionValue;
        const optionIndex = parseInt(swatch.dataset.optionIndex);

        this.selectOption(optionIndex, optionValue);
      });
    });
  }

  selectOption(optionIndex, optionValue) {
    // Update selected options array
    this.selectedOptions[optionIndex] = optionValue;

    // Update UI - remove active class from all options at this index
    const optionContainer = this.container.querySelector(`[data-option-index="${optionIndex}"]`);
    if (optionContainer) {
      const allOptions = optionContainer.querySelectorAll('[data-option-value]');
      allOptions.forEach(opt => opt.classList.remove('active'));

      // Add active class to selected option
      const selectedElement = optionContainer.querySelector(`[data-option-value="${optionValue}"]`);
      if (selectedElement) {
        selectedElement.classList.add('active');
      }
    }

    // Update selected value display
    const selectedValueDisplay = this.container.querySelector(`[data-selected-value-${optionIndex}]`);
    if (selectedValueDisplay) {
      selectedValueDisplay.textContent = optionValue;
    }

    // Update summary
    const summaryValue = this.container.querySelector(`[data-summary-value-${optionIndex}]`);
    if (summaryValue) {
      summaryValue.textContent = optionValue;
    }

    // Find and update variant
    this.updateVariant();

    // Dispatch custom event
    this.dispatchChangeEvent();
  }

  updateVariant() {
    const variant = this.findVariantByOptions(this.selectedOptions);
    this.currentVariant = variant;

    if (variant) {
      // Update hidden variant ID input
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      // Update price
      this.updatePrice(variant);

      // Update availability
      this.updateAvailability(variant);

      // Update add to cart button
      this.updateAddToCartButton(variant);

      // Update product images if variant has featured image
      if (variant.featured_image) {
        this.updateProductImage(variant.featured_image);
      }
    }
  }

  findVariantByOptions(options) {
    const variants = this.productData.variants || [];

    return variants.find(variant => {
      if (!variant.options || variant.options.length !== options.length) {
        return false;
      }

      return variant.options.every((option, index) => {
        return option === options[index];
      });
    });
  }

  updatePrice(variant) {
    const priceElement = document.querySelector('.product__price-regular');
    const comparePriceElement = document.querySelector('.product__price-compare');

    if (priceElement && variant.price) {
      const formattedPrice = this.formatMoney(variant.price);
      priceElement.textContent = formattedPrice;
    }

    if (comparePriceElement && variant.compare_at_price) {
      const formattedComparePrice = this.formatMoney(variant.compare_at_price);
      comparePriceElement.textContent = formattedComparePrice;
      comparePriceElement.style.display = variant.compare_at_price > variant.price ? 'inline' : 'none';
    } else if (comparePriceElement) {
      comparePriceElement.style.display = 'none';
    }
  }

  updateAvailability(variant) {
    const availabilityContainer = this.container.querySelector('[data-configurator-availability]');
    const availabilityMessage = this.container.querySelector('[data-availability-message]');

    if (!availabilityMessage) return;

    if (variant.available) {
      availabilityMessage.classList.remove('out-of-stock');
      availabilityMessage.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="var(--color-primary)"/>
        </svg>
        <span>In Stock - Ready to Ship</span>
      `;
    } else {
      availabilityMessage.classList.add('out-of-stock');
      availabilityMessage.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="#ff5555"/>
        </svg>
        <span>Out of Stock - Notify Me When Available</span>
      `;
    }
  }

  updateAddToCartButton(variant) {
    const addToCartBtn = document.querySelector('.product__add-to-cart');

    if (!addToCartBtn) return;

    if (variant.available) {
      addToCartBtn.disabled = false;
      addToCartBtn.textContent = 'Add to Cart';
    } else {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Sold Out';
    }
  }

  updateProductImage(featuredImage) {
    const mainImage = document.getElementById('product-featured-image');

    if (mainImage && featuredImage.src) {
      mainImage.src = featuredImage.src;
      mainImage.alt = featuredImage.alt || this.productData.title;
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents);
    }

    const dollars = cents / 100;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  dispatchChangeEvent() {
    const event = new CustomEvent('configurator:change', {
      detail: {
        selectedOptions: this.selectedOptions,
        currentVariant: this.currentVariant
      }
    });

    document.dispatchEvent(event);
  }

  // Public API
  getSelectedOptions() {
    return this.selectedOptions;
  }

  getCurrentVariant() {
    return this.currentVariant;
  }

  setOption(optionIndex, optionValue) {
    this.selectOption(optionIndex, optionValue);
  }
}

// Initialize configurator when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const configuratorContainer = document.querySelector('[data-product-configurator]');

  if (configuratorContainer && typeof window.productData !== 'undefined') {
    window.productConfigurator = new ProductConfigurator(
      configuratorContainer,
      window.productData
    );
  }
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductConfigurator;
}
